import c from 'classnames';
import propsFromUrl from 'core/enhancers/propsFromUrl';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { WithStyles, ITheme } from 'core/style/interfaces';
import React from 'react';
import { compose } from 'recompose';
import ArrowLeftIcon from '../../../core/svg/arrow_left.svg';
import dropDownHoc, { styles as dropdownStyles } from '../../../core/components/dropdown/dropDownHoc';
import dropDownSelfStateHoc from '../../../core/components/dropdown/dropDownSelfStateHoc';
import { complexityOptions, priceOptions } from './const';
import DropDownMobileFilterButton from './DropDownMobileFilterButton';

const DropDown = dropDownSelfStateHoc({
    DropDown: dropDownHoc({
        DropDownButton: DropDownMobileFilterButton,
    }),
});

type IDropDownMobileFilterState = {
    step: 'main' | 'price' | 'complexity';
};

const styles = (theme: ITheme) =>
    createStyles({
        dropdown: {
            ...dropdownStyles.dropdown,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            fontSize: 0.9 + 'rem',
            border: '1px solid #cccccc',
            width: '45%',
            cursor: 'pointer',
        },
        dropdownList: {
            ...dropdownStyles.dropdownList,
            width: 320,
            left: -1,
            boxSizing: 'border-box',
            border: '1px solid #cccccc',
            background: 'white',
            overflowY: 'auto',
            padding: 0,

            [theme.breakpoints.down(350)]: {
                width: 'calc(100vw - 30px)',
            },
        },
        itemContainer: {
            height: 57,
            display: 'flex',
            borderBottom: '1px solid #cccccc',
            alignItems: 'center',

            '&:last-child': {
                borderBottom: 'none',
            },
        },
        itemTitle: {
            flex: 1,
            fontSize: 1 + 'rem',
            paddingLeft: 35,
        },
        itemTitleSelected: {
            fontWeight: 600,
        },
        itemTitleNoPadding: {
            paddingLeft: 0,
        },
        icon: {
            height: 1.1 + 'rem',
            width: 1.1 + 'rem',
            fill: '#333333',
        },
        iconArrowRight: {
            transform: 'rotate(180deg)',
        },
        iconContainer: {
            padding: '0px 25px',
            display: 'flex',
            alignItems: 'center',
        },
        resetButtonContainer: {
            display: 'flex',
            paddingTop: 15,
            paddingBottom: 15,
            justifyContent: 'center',
        },
        resetButton: {
            fontSize: 0.9 + 'rem',
            border: '1px solid #cccccc',
            fontWeight: 600,
            padding: '10px 30px',
        },
    });

class DropDownMobileFilter extends React.PureComponent<IInnerProps, IDropDownMobileFilterState> {
    state: IDropDownMobileFilterState = {
        step: 'main',
    };

    renderMethodByStep: { [type in IDropDownMobileFilterState['step']]: () => JSX.Element } = {
        main: () => this.renderMainStep(),
        price: () => this.renderPriceStep(),
        complexity: () => this.renderComplexityStep(),
    };

    changeStep = (step: IDropDownMobileFilterState['step']) => e => {
        e.nativeEvent.stopImmediatePropagation();
        this.setState({ step });
    };

    onFilterChange = (value: { [property: string]: string }) => () => {
        this.props.onFilterChange(value);
        this.setState({ step: 'main' });
    };

    onReset = () => {
        this.props.onFilterChange({
            complexity: undefined,
            isFree: undefined,
        });
    };

    renderMainStep() {
        const { classes, isFree, complexity, isFreeFilterVisible, complexityFilterVisible } = this.props;
        const priceValue = priceOptions.find(opt => opt.value === isFree);
        const complexityValue = complexityOptions.find(opt => opt.value === complexity);

        return (
            <div>
                {isFreeFilterVisible && (
                    <div className={classes.itemContainer} onClick={this.changeStep('price')}>
                        <div className={classes.itemTitle}>
                            Цена
                            {priceValue.value && (
                                <span>
                                    : <b>{priceValue.label}</b>
                                </span>
                            )}
                        </div>
                        <div className={classes.iconContainer}>
                            <ArrowLeftIcon className={c(classes.icon, classes.iconArrowRight)} />
                        </div>
                    </div>
                )}
                {complexityFilterVisible && (
                    <div className={classes.itemContainer} onClick={this.changeStep('complexity')}>
                        <div className={classes.itemTitle}>
                            Уровень
                            {complexityValue.value && (
                                <span>
                                    : <b>{complexityValue.label}</b>
                                </span>
                            )}
                        </div>
                        <div className={classes.iconContainer}>
                            <ArrowLeftIcon className={c(classes.icon, classes.iconArrowRight)} />
                        </div>
                    </div>
                )}
                {(isFree || complexity) && (
                    <div className={classes.resetButtonContainer}>
                        <div className={c(classes.resetButton, 'js-force-close')} onClick={this.onReset}>
                            Сбросить все
                        </div>
                    </div>
                )}
            </div>
        );
    }

    renderPriceStep() {
        const { classes, isFree } = this.props;

        return (
            <div>
                <div className={classes.itemContainer} onClick={this.changeStep('main')}>
                    <div className={classes.iconContainer}>
                        <ArrowLeftIcon className={classes.icon} />
                    </div>
                    <div className={c(classes.itemTitle, classes.itemTitleNoPadding)}>Вернуться к фильтрам</div>
                </div>
                {priceOptions.map(opt => (
                    <div
                        key={opt.label}
                        className={c(classes.itemContainer, 'js-force-close')}
                        onClick={this.onFilterChange({ isFree: opt.value })}
                    >
                        <div
                            className={c(classes.itemTitle, {
                                [classes.itemTitleSelected]: isFree === opt.value,
                            })}
                        >
                            {opt.label}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    renderComplexityStep() {
        const { classes, complexity } = this.props;

        return (
            <div>
                <div className={classes.itemContainer} onClick={this.changeStep('main')}>
                    <div className={classes.iconContainer}>
                        <ArrowLeftIcon className={classes.icon} />
                    </div>
                    <div className={c(classes.itemTitle, classes.itemTitleNoPadding)}>Вернуться к фильтрам</div>
                </div>
                {complexityOptions.map(opt => (
                    <div
                        key={opt.label}
                        className={c(classes.itemContainer, 'js-force-close')}
                        onClick={this.onFilterChange({ complexity: opt.value })}
                    >
                        <div
                            className={c(classes.itemTitle, {
                                [classes.itemTitleSelected]: complexity === opt.value,
                            })}
                        >
                            {opt.label}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    render() {
        const { classes } = this.props;
        const { step } = this.state;

        return (
            <DropDown
                classes={{
                    dropdown: classes.dropdown,
                    dropdownList: classes.dropdownList,
                }}
                id="mobile-filter-courses"
            >
                {this.renderMethodByStep[step]()}
            </DropDown>
        );
    }
}

export type IOutterProps = {
    onFilterChange: (newFilter) => void;
    isFreeFilterVisible: boolean;
    complexityFilterVisible: boolean;
};

export type IInnerProps = {
    isFree: string;
    complexity: string;
} & IOutterProps &
    WithStyles<typeof styles>;

export default compose<IInnerProps, IOutterProps>(
    propsFromUrl({
        query: ['isFree', 'complexity'],
    }),
    injectSheet(styles),
)(DropDownMobileFilter);
